import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-kann-base bg-dots flex items-center justify-center p-6 relative overflow-hidden" data-testid="login-page">
      <div className="glow-orb absolute pointer-events-none" style={{ width: 520, height: 520, top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
      <div className="relative w-full max-w-sm animate-fade-up">
        <div className="text-center">
          <div className="logo-font text-5xl text-kann-text-pri">KANN<span className="text-kann-gold">.</span></div>
          <p className="text-kann-text-sec mt-3 text-sm leading-relaxed">Brand kits, flyers, AI copy &amp; imagery, and interactive 3D assembly manuals — one workspace for the whole KANN production workflow.</p>
        </div>

        <div className="mt-9 bg-kann-surface/70 backdrop-blur-sm border border-kann-border/30 rounded-2xl p-7">
          <h1 className="text-xl font-medium text-kann-text-pri">Welcome back</h1>
          <p className="text-kann-text-sec mt-1 text-sm">Sign in to continue.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold focus:ring-1 focus:ring-kann-gold px-3 py-2.5 text-sm outline-none" placeholder="you@kann.studio" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Password</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold focus:ring-1 focus:ring-kann-gold px-3 py-2.5 text-sm outline-none" placeholder="••••••••" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-red-400">{error}</div>}
            <button data-testid="login-submit" disabled={busy}
              className="w-full bg-kann-ink text-white font-semibold rounded-full py-3 text-sm hover:bg-black transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
